import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Save, X, Monitor, Eye, Download, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { insertExampleAds } from '@/utils/adService';
import { handleConnectionError } from '@/utils/connectionErrorHandler';

interface Ad {
  id: string;
  name: string;
  type: string;
  position: string;
  content?: string;
  image_url?: string;
  link_url?: string;
  active: boolean;
  start_date?: string;
  end_date?: string;
  created_at: string;
  updated_at: string;
}

interface NewAd {
  name: string;
  type: string;
  position: string;
  content: string;
  image_url: string;
  link_url: string;
  active: boolean;
  start_date: string;
  end_date: string;
}

const AdminAds = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingAd, setIsAddingAd] = useState(false);
  const [editingAd, setEditingAd] = useState<Ad | null>(null);
  const [isEditingAd, setIsEditingAd] = useState(false);
  const [newAd, setNewAd] = useState<NewAd>({
    name: '',
    type: 'banner',
    position: 'header',
    content: '',
    image_url: '',
    link_url: '',
    active: true,
    start_date: '',
    end_date: '',
  });
  const queryClient = useQueryClient();

  const adTypes = [
    { value: 'banner', label: 'Banner' },
    { value: 'popup', label: 'Popup' },
    { value: 'sidebar', label: 'Sidebar' },
    { value: 'inline', label: 'Inline' },
    { value: 'video', label: 'Video' },
  ];

  const adPositions = [
    { value: 'header', label: 'Header' },
    { value: 'footer', label: 'Footer' },
    { value: 'sidebar', label: 'Sidebar' },
    { value: 'before-game', label: 'Before Game' },
    { value: 'after-game', label: 'After Game' },
    { value: 'between-games', label: 'Between Games' },
    { value: 'blog-above-post', label: 'Blog - Above Post' },
    { value: 'blog-related-games', label: 'Blog - Related Games' },
  ];

  const { data: ads = [], isLoading, error, refetch } = useQuery({
    queryKey: ['admin-ads', searchTerm],
    queryFn: async () => {
      try {
        console.log('Fetching ads...');
        
        // Add timeout to prevent stuck requests
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Ads fetch timeout')), 10000);
        });
        
        let query = supabase
          .from('ads')
          .select('*')
          .order('created_at', { ascending: false });

        if (searchTerm) {
          query = query.ilike('name', `%${searchTerm}%`);
        }

        const fetchPromise = query;
        const { data, error } = await Promise.race([fetchPromise, timeoutPromise]) as any;
        
        if (error) {
          handleConnectionError(error, 'AdminAds fetch');
          throw error;
        }
        
        console.log('Ads fetched successfully:', data?.length || 0);
        return (data || []) as Ad[];
      } catch (err) {
        console.error('Failed to fetch ads:', err);
        handleConnectionError(err as Error, 'AdminAds fetch');
        throw err;
      }
    },
    retry: 2,
    staleTime: 30000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    networkMode: 'online',
  });

  const createAdMutation = useMutation({
    mutationFn: async (ad: NewAd) => {
      const adData = {
        ...ad,
        start_date: ad.start_date || null,
        end_date: ad.end_date || null,
      };
      const { error } = await supabase
        .from('ads')
        .insert([adData]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ads'] });
      toast.success('Ad created successfully');
      setIsAddingAd(false);
      setNewAd({
        name: '',
        type: 'banner',
        position: 'header',
        content: '',
        image_url: '',
        link_url: '',
        active: true,
        start_date: '',
        end_date: '',
      });
    },
    onError: (error: any) => {
      console.error('Create ad error:', error);
      handleConnectionError(error, 'AdminAds create');
      toast.error(`Failed to create ad: ${error.message || 'Unknown error'}`);
    },
  });

  const updateAdMutation = useMutation({
    mutationFn: async (ad: Ad) => {
      const adData = {
        name: ad.name,
        type: ad.type,
        position: ad.position,
        content: ad.content || null,
        image_url: ad.image_url || null,
        link_url: ad.link_url || null,
        active: ad.active,
        start_date: ad.start_date || null,
        end_date: ad.end_date || null,
      };
      const { error } = await supabase
        .from('ads')
        .update(adData)
        .eq('id', ad.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ads'] });
      toast.success('Ad updated successfully');
      setIsEditingAd(false);
      setEditingAd(null);
    },
    onError: (error: any) => {
      console.error('Update ad error:', error);
      handleConnectionError(error, 'AdminAds update');
      toast.error(`Failed to update ad: ${error.message || 'Unknown error'}`);
    },
  });

  const deleteAdMutation = useMutation({
    mutationFn: async (adId: string) => {
      const { error } = await supabase
        .from('ads')
        .delete()
        .eq('id', adId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ads'] });
      toast.success('Ad deleted successfully');
    },
    onError: (error: any) => {
      console.error('Delete ad error:', error);
      handleConnectionError(error, 'AdminAds delete');
      toast.error(`Failed to delete ad: ${error.message || 'Unknown error'}`);
    },
  });

  const insertExampleAdsMutation = useMutation({
    mutationFn: insertExampleAds,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ads'] });
      toast.success('Example AdSense ads inserted successfully');
    },
    onError: (error: any) => {
      console.error('Insert example ads error:', error);
      handleConnectionError(error, 'AdminAds insert examples');
      toast.error('Failed to insert example ads');
    },
  });

  const handleCreateAd = () => {
    if (!newAd.name) {
      toast.error('Ad name is required');
      return;
    }
    createAdMutation.mutate(newAd);
  };

  const handleEditAd = (ad: Ad) => {
    setEditingAd(ad);
    setIsEditingAd(true);
  };

  const handleUpdateAd = () => {
    if (!editingAd || !editingAd.name) {
      toast.error('Ad name is required');
      return;
    }
    updateAdMutation.mutate(editingAd);
  };

  const handleDeleteAd = (adId: string) => {
    if (window.confirm('Are you sure you want to delete this ad?')) {
      deleteAdMutation.mutate(adId);
    }
  };

  const handleInsertExampleAds = () => {
    if (window.confirm('This will add 6 example AdSense ads to your database. Continue?')) {
      insertExampleAdsMutation.mutate();
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isAdActive = (ad: Ad) => {
    if (!ad.active) return false;
    const now = new Date();
    if (ad.start_date && new Date(ad.start_date) > now) return false;
    if (ad.end_date && new Date(ad.end_date) < now) return false;
    return true;
  };

  const handleManualRefresh = () => {
    refetch();
    toast.info('Refreshing ads data...');
  };

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <Card className="p-6 text-center">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Error Loading Ads</h3>
          <p className="text-gray-600 mb-4">{error.message || 'Failed to load ads'}</p>
          <div className="flex gap-2 justify-center">
            <Button onClick={handleManualRefresh}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Ads Management</h1>
          <p className="text-gray-600 mt-1">Manage your website advertisements</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button 
            onClick={handleManualRefresh}
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={handleInsertExampleAds}
            disabled={insertExampleAdsMutation.isPending}
            variant="outline"
            className="w-full sm:w-auto"
          >
            <Download className="w-4 h-4 mr-2" />
            {insertExampleAdsMutation.isPending ? 'Adding...' : 'Add AdSense Examples'}
          </Button>
          <Dialog open={isAddingAd} onOpenChange={setIsAddingAd}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Add New Ad
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Advertisement</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={newAd.name}
                    onChange={(e) => setNewAd(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ad name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <select
                    id="type"
                    value={newAd.type}
                    onChange={(e) => setNewAd(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {adTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <select
                    id="position"
                    value={newAd.position}
                    onChange={(e) => setNewAd(prev => ({ ...prev, position: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {adPositions.map((position) => (
                      <option key={position.value} value={position.value}>
                        {position.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="image_url">Image URL</Label>
                  <Input
                    id="image_url"
                    value={newAd.image_url}
                    onChange={(e) => setNewAd(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://example.com/ad.jpg"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="link_url">Link URL</Label>
                  <Input
                    id="link_url"
                    value={newAd.link_url}
                    onChange={(e) => setNewAd(prev => ({ ...prev, link_url: e.target.value }))}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="datetime-local"
                    value={newAd.start_date}
                    onChange={(e) => setNewAd(prev => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="datetime-local"
                    value={newAd.end_date}
                    onChange={(e) => setNewAd(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="content">Ad Code/Content</Label>
                  <Textarea
                    id="content"
                    value={newAd.content}
                    onChange={(e) => setNewAd(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="HTML/JavaScript ad code"
                    rows={4}
                  />
                </div>
                <div className="md:col-span-2 flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={newAd.active}
                    onCheckedChange={(checked) => setNewAd(prev => ({ ...prev, active: checked }))}
                  />
                  <Label htmlFor="active">Active</Label>
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button 
                  onClick={handleCreateAd} 
                  disabled={createAdMutation.isPending}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {createAdMutation.isPending ? 'Creating...' : 'Create Ad'}
                </Button>
                <Button variant="outline" onClick={() => setIsAddingAd(false)}>
                  Cancel
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Ad Dialog */}
      <Dialog open={isEditingAd} onOpenChange={setIsEditingAd}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Advertisement</DialogTitle>
          </DialogHeader>
          {editingAd && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name *</Label>
                <Input
                  id="edit-name"
                  value={editingAd.name}
                  onChange={(e) => setEditingAd(prev => prev ? { ...prev, name: e.target.value } : null)}
                  placeholder="Ad name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-type">Type</Label>
                <select
                  id="edit-type"
                  value={editingAd.type}
                  onChange={(e) => setEditingAd(prev => prev ? { ...prev, type: e.target.value } : null)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {adTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-position">Position</Label>
                <select
                  id="edit-position"
                  value={editingAd.position}
                  onChange={(e) => setEditingAd(prev => prev ? { ...prev, position: e.target.value } : null)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {adPositions.map((position) => (
                    <option key={position.value} value={position.value}>
                      {position.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-image_url">Image URL</Label>
                <Input
                  id="edit-image_url"
                  value={editingAd.image_url || ''}
                  onChange={(e) => setEditingAd(prev => prev ? { ...prev, image_url: e.target.value } : null)}
                  placeholder="https://example.com/ad.jpg"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-link_url">Link URL</Label>
                <Input
                  id="edit-link_url"
                  value={editingAd.link_url || ''}
                  onChange={(e) => setEditingAd(prev => prev ? { ...prev, link_url: e.target.value } : null)}
                  placeholder="https://example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-start_date">Start Date</Label>
                <Input
                  id="edit-start_date"
                  type="datetime-local"
                  value={editingAd.start_date ? new Date(editingAd.start_date).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setEditingAd(prev => prev ? { ...prev, start_date: e.target.value } : null)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-end_date">End Date</Label>
                <Input
                  id="edit-end_date"
                  type="datetime-local"
                  value={editingAd.end_date ? new Date(editingAd.end_date).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setEditingAd(prev => prev ? { ...prev, end_date: e.target.value } : null)}
                />
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="edit-content">Ad Code/Content</Label>
                <Textarea
                  id="edit-content"
                  value={editingAd.content || ''}
                  onChange={(e) => setEditingAd(prev => prev ? { ...prev, content: e.target.value } : null)}
                  placeholder="HTML/JavaScript ad code"
                  rows={4}
                />
              </div>
              <div className="md:col-span-2 flex items-center space-x-2">
                <Switch
                  id="edit-active"
                  checked={editingAd.active}
                  onCheckedChange={(checked) => setEditingAd(prev => prev ? { ...prev, active: checked } : null)}
                />
                <Label htmlFor="edit-active">Active</Label>
              </div>
            </div>
          )}
          <div className="flex gap-2 mt-6">
            <Button 
              onClick={handleUpdateAd} 
              disabled={updateAdMutation.isPending}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {updateAdMutation.isPending ? 'Updating...' : 'Update Ad'}
            </Button>
            <Button variant="outline" onClick={() => setIsEditingAd(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Search */}
      <Card className="p-4 sm:p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search ads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </Card>

      {/* Ads Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad</TableHead>
                <TableHead className="hidden md:table-cell">Type & Position</TableHead>
                <TableHead className="hidden lg:table-cell">Status</TableHead>
                <TableHead className="hidden lg:table-cell">Schedule</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="flex flex-col items-center space-y-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <p className="text-sm text-gray-500">Loading ads...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : ads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No ads found
                  </TableCell>
                </TableRow>
              ) : (
                ads.map((ad) => (
                  <TableRow key={ad.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Monitor className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{ad.name}</div>
                          <div className="text-sm text-gray-500 md:hidden">{ad.type} • {ad.position}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="space-y-1">
                        <Badge variant="outline">{ad.type}</Badge>
                        <div className="text-sm text-gray-500">{ad.position}</div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Badge variant={isAdActive(ad) ? "default" : "secondary"}>
                        {isAdActive(ad) ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="text-sm text-gray-600">
                        {ad.start_date && <div>Start: {formatDate(ad.start_date)}</div>}
                        {ad.end_date && <div>End: {formatDate(ad.end_date)}</div>}
                        {!ad.start_date && !ad.end_date && <div>Always active</div>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button size="sm" variant="ghost" className="hidden sm:inline-flex">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleEditAd(ad)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteAd(ad.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default AdminAds;
